import { isMobile } from "shared/lib/isMobile";

import { listReasonCodes } from "../../cbt/api/reasonCodes";
import { getCbtData } from "../../data/actions/cbt";
import { getCbtToken } from "../../data/customerTokens";
import { fetchAcquisitionParams } from "../../lib/golCopy";
import { getFieldsFromSections } from "./getFieldsFromSections";
import { getSectionsFromAcquisitionParamsBookingForm } from "./getSectionsFromAcquisitionParamsBookingForm";
import { getSectionsWithFlatElementsFromSections } from "./getSectionsWithFlatElementsFromSections";

export async function fetchBookingFormFromAcquisitionParams({
  refs,
  fullPrice,
  cbt,
  query,
  customerUsername,
  cbtApiUrl,
  intl,
  isFlight,
  isHotel,
  defaultCountry,
  promoCode,
}) {
  const acquisitionData = await fetchAcquisitionParams({
    refs,
    fullPrice,
    defaultCountry,
    promoCode,
    alternativeCurrency: query?.currency,
  });

  const acquisitionParamsBookingForm =
    acquisitionData?.bookingForm?.BookingFormGroup;

  const acquisitionParamsBookingFormAlert =
    acquisitionData?.bookingFormAlerts?.BookingFormAlert?.[0];

  const {
    sections,
    fields,
  } = await getSectionsAndFieldsFromAcquisitionParamsBookingForm({
    acquisitionParamsBookingForm,
    acquisitionParamsBookingFormAlert,
    cbt,
    query,
    customerUsername,
    cbtApiUrl,
    intl,
    isFlight,
    isHotel,
  });

  return {
    acquisitionParamsBookingForm,
    sections,
    fields,
    codeBook: acquisitionData.codebook,
    paymentFormMethods: acquisitionData.paymentFormMethods,
    alternativeCurrencyPaymentFormMethods:
      acquisitionData.alternativeCurrencyPaymentFormMethods,
    paymentFormMethodForms:
      acquisitionData?.codebook?.PaymentformMethodForms
        ?.PaymentformMethodForm || [],
    ticketingCombinations: acquisitionData.ticketingCombinations,
  };
}

export async function getSectionsAndFieldsFromAcquisitionParamsBookingForm({
  acquisitionParamsBookingForm,
  acquisitionParamsBookingFormAlert,
  cbt,
  query,
  customerUsername,
  cbtApiUrl,
  intl,
  isFlight,
  isHotel,
}) {
  let modifiedSections = [];

  const sectionsBasic = getSectionsFromAcquisitionParamsBookingForm(
    acquisitionParamsBookingForm,
    cbt,
    acquisitionParamsBookingFormAlert
  );

  const { sectionsWithCbtReasonCode } = await getSectionsWithCbtReasonCode({
    sectionsBasic,
    query,
    customerUsername,
    cbtApiUrl,
    intl,
    isFlight,
    isHotel,
  });

  const sectionsWithGradeField = await addGradeFieldToGuestSections({
    sections: sectionsWithCbtReasonCode,
    cbt,
    intl,
    query,
  });

  modifiedSections = sectionsWithGradeField;

  const fields = getFieldsFromSections(modifiedSections);

  return {
    sections: getSectionsWithFlatElementsFromSections(modifiedSections),
    fields,
  };
}

async function getSectionsWithCbtReasonCode({
  sectionsBasic,
  query,
  customerUsername,
  cbtApiUrl,
  intl,
  isFlight,
  isHotel,
}) {
  if (query?.policyFailed !== "true" && query?.policyFailed !== true) {
    return {
      sectionsWithCbtReasonCode: sectionsBasic,
      reasonCodeOptions: null,
    };
  }

  const cbtToken = await getCbtToken();
  const reasonCodes = getCbtData(
    await listReasonCodes({
      cbtToken,
      customerUsername,
      cbtApiUrl,
      onlyFlight: isFlight,
      onlyHotel: isHotel,
    })
  ).sort((a, b) => a.Name.localeCompare(b.Name));

  const reasonCodeSection = {
    title: intl.formatMessage({
      id: "Result.cbtReasonForViolationPolicy",
    }),
    elements: [
      {
        required: true,
        type: "select",
        name: "cbt-reason-code",
        label: intl.formatMessage({
          id: "Result.cbtReasonCode",
        }),
        multiple: false,
        options: reasonCodes.map(({ Id, Name, Description }) => ({
          value: Id,
          label: Description ? `${Name} - ${Description}` : Name,
        })),
      },
    ],
    code: "reasonCode",
  };

  return {
    sectionsWithCbtReasonCode: [reasonCodeSection, ...sectionsBasic],
    reasonCodeOptions: reasonCodes.map(({ Id, Name, Description }) => ({
      value: Id,
      label: Description ? `${Name} - ${Description}` : Name,
    })),
  };
}

async function addGradeFieldToGuestSections({ sections, cbt, intl, query }) {
  if (isMobile()) {
    return sections;
  }

  if (!query.travelReasonId && !query.policyResolvedId) {
    return sections;
  }

  let indexPassenger = 0;
  return sections.map((section) => {
    const isGuest =
      indexPassenger >= cbt.passengersForm.travellers.length &&
      (section?.code === "passengerPerson" || section?.code === "passenger");

    const actualIndexPassenger = indexPassenger;

    if (section?.code === "passengerPerson") {
      indexPassenger += 1;
    }

    if (isGuest) {
      const parsedTypeFromTitle = section.title.split("-").pop();

      const filteredGradesForType = cbt.grades.filter(
        (grade) =>
          grade.PassengerTypeCode === parsedTypeFromTitle &&
          grade.EnableForGuests === true
      );

      const gradeField = {
        required: false,
        type: "select",
        name: `${actualIndexPassenger}-passenger_grade`,
        options: filteredGradesForType.map(({ Id, Name }) => ({
          value: Id,
          label: Name,
        })),
        label: intl.formatMessage({
          id: "Result.grade",
        }),
        multiple: false,
      };

      const titleIndex = section.elements.findIndex(
        (element) => element.name && element.name.includes("_title")
      );

      if (titleIndex !== -1) {
        return {
          ...section,
          elements: [
            ...section.elements.slice(0, titleIndex + 1),
            gradeField,
            ...section.elements.slice(titleIndex + 1),
          ],
        };
      }

      return {
        ...section,
        elements: [...section.elements, gradeField],
      };
    }

    return section;
  });
}
